import { useState, useRef, useEffect } from "react";

import {
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";

const DateRangePicker = ({ startDate, endDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Current");
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const dropdownRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCalendar(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDateRange = (start, end) => {
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString().slice(-2);
      return `${day}/${month}/${year}`;
    };

    const startDay = start.toLocaleDateString("en-US", { weekday: "short" });
    const endDay = end.toLocaleDateString("en-US", { weekday: "short" });

    return `${startDay} ${formatDate(start)} - ${endDay} ${formatDate(end)}`;
  };

  const presetRanges = {
    Today: () => {
      const today = new Date();
      return { start: startOfDay(today), end: endOfDay(today) };
    },
    Yesterday: () => {
      const yesterday = subDays(new Date(), 1);
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    },
    Week: () => {
      const today = new Date();
      return {
        start: startOfWeek(today, { weekStartsOn: 0 }),
        end: endOfWeek(today, { weekStartsOn: 0 }),
      };
    },
    "7 Days": () => {
      const today = new Date();
      return { start: subDays(today, 6), end: today };
    },
    "14 Days": () => {
      const today = new Date();
      return { start: subDays(today, 13), end: today };
    },
    "30 Days": () => {
      const today = new Date();
      return { start: subDays(today, 29), end: today };
    },
    "90 Days": () => {
      const today = new Date();
      return { start: subDays(today, 89), end: today };
    },
    "Fortnight 1 (Mon 19/05 - Sun 01/06)": () => {
      return {
        start: new Date("2025-05-19"),
        end: new Date("2025-06-01"),
      };
    },
    "Fortnight 2 (Mon 12/05 - Sun 25/05)": () => {
      return {
        start: new Date("2025-05-12"),
        end: new Date("2025-05-25"),
      };
    },
    Month: () => {
      const today = new Date();
      return { start: startOfMonth(today), end: endOfMonth(today) };
    },
  };

  const handlePresetSelect = (preset) => {
    const range = presetRanges[preset]();
    onDateChange(range.start, range.end);
    setIsOpen(false);
  };

  const handleCustomDateSelect = () => {
    setShowCalendar(true);
  };

  const applyCustomDates = () => {
    onDateChange(tempStartDate, tempEndDate);
    setShowCalendar(false);
    setIsOpen(false);
  };

  const renderCalendar = (date, isStart) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const today = new Date();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      days.push(currentDate);
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div className="w-80">
        <div className="flex items-center justify-between px-4 py-1 rounded-lg bg-gray-100 text-gray-600">
          <button
            onClick={() => {
              const newDate = new Date(date);
              newDate.setMonth(newDate.getMonth() - 1);
              if (isStart) setTempStartDate(newDate);
              else setTempEndDate(newDate);
            }}
            className="p-1 hover:bg-white rounded"
          >
            <i className="fa fa-chevron-left"></i>
          </button>
          <h3 className="font-semibold">
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={() => {
              const newDate = new Date(date);
              newDate.setMonth(newDate.getMonth() + 1);
              if (isStart) setTempStartDate(newDate);
              else setTempEndDate(newDate);
            }}
            className="p-1 hover:bg-white rounded"
          >
            <i className="fa fa-chevron-right"></i>
          </button>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su","Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-center text-sm font-medium text-gray-600 p-2"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              const isCurrentMonth = day.getMonth() === month;
              const isSelected =
                (isStart &&
                  day.toDateString() === tempStartDate.toDateString()) ||
                (!isStart && day.toDateString() === tempEndDate.toDateString());
              const isToday = day.toDateString() === today.toDateString();

              return (
                <button
                  key={index}
                  onClick={() => {
                    if (isStart) setTempStartDate(day);
                    else setTempEndDate(day);
                  }}
                  className={`
                    p-2 text-sm rounded transition-colors
                    ${
                      !isCurrentMonth
                        ? "text-gray-300"
                        : "text-gray-700 hover:bg-gray-100"
                    }
                    ${isSelected ? "bg-gray-600 text-white" : ""}
                    ${isToday && !isSelected ? "bg-gray-100 text-gray-600" : ""}
                  `}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-80 px-2 py-2 bg-gray-100 rounded-md ml-2 shadow-sm border border-gray-300 hover:border-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500"
      >
        <span className="text-gray-600 font-semibold text-[12px]">
          {formatDateRange(startDate, endDate)}
        </span>
        <i
          className={`fa fa-chevron-${
            isOpen ? "up" : "down"
          } text-gray-300 text-xs transition-transform`}
        ></i>
      </button>

      {isOpen && !showCalendar && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-lg z-50 overflow-hidden animate-fade-in">
          <div className="flex divide-x divide-gray-100">
            {/* Left Panel */}
            <div className="w-48 bg-gray-50">
              <div
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setSelectedOption("Current")}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 font-medium">
                    Current
                  </span>
                  <i className="fa fa-chevron-right text-gray-300 text-xs"></i>
                </div>
              </div>
              <div
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-t border-gray-100"
                onClick={() => setSelectedOption("Last")}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 font-medium">
                    Last
                  </span>
                  <i className="fa fa-chevron-right text-gray-300 text-xs"></i>
                </div>
              </div>
              <div
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-t border-gray-100"
                onClick={handleCustomDateSelect}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 font-medium">
                    Custom Range
                  </span>
                  <i className="fa fa-chevron-right text-gray-300 text-xs"></i>
                </div>
              </div>
            </div>

            {/* Right Panel */}
            <div className="w-64">
              {selectedOption === "Current" && (
                <div className="divide-y divide-gray-100">
                  {["Today", "Week", "Month"].map((preset) => (
                    <div
                      key={preset}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handlePresetSelect(preset)}
                    >
                      <span className="text-sm text-gray-600">{preset}</span>
                    </div>
                  ))}
                </div>
              )}
              {selectedOption === "Last" && (
                <div className="divide-y divide-gray-100">
                  {["Yesterday", "7 Days", "14 Days", "30 Days", "90 Days"].map(
                    (preset) => (
                      <div
                        key={preset}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handlePresetSelect(preset)}
                      >
                        <span className="text-sm text-gray-600">{preset}</span>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCalendar && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-lg z-50 animate-fade-in">
          <div className="p-5">
            <div className="flex gap-5">
              <div className="flex-1">
                <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                  Start Date
                </h4>
                {renderCalendar(tempStartDate, true)}
              </div>
              <div className="flex-1">
                <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                  End Date
                </h4>
                {renderCalendar(tempEndDate, false)}
              </div>
            </div>
            <div className="flex justify-end mt-5 pt-5 border-t border-gray-100">
              <button
                onClick={applyCustomDates}
                className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-100 text-sm font-medium"
              >
                Apply Dates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
